import { Grid, Stack, Typography, Autocomplete, TextField } from "@mui/material";
import CustomButton from "components/atoms/CustomButton";
import CustomTextInput from "components/atoms/CustomTextInput";
import CustomDatePicker from "components/atoms/DatePicker";
import ImageUpload from "components/atoms/ImageUpload";
import { useFormik } from "formik";
import React, { useMemo } from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { useCustomMutation } from "store/hooks/react-query/mutate/useMutateFunc";
import { useCustomQuery } from "store/hooks/react-query/query/useQuery";
import { handleErrorProps } from "utils/handleErrorProps";
import {
  GET_ALL_PHARMACY_PRODUCTS,
  GET_DRUG_TYPES,
  GET_PHARMACY_GENERICS,
} from "utils/reactQueryKeys";

const defaultInitialValues = {
  drugName: "",
  drugType: "",
  unit: "",
  genericName: "",
  strength: "",
  brandName: "",
  prodcutDescription: "",
  filename: "",
  salesPrice: "",
  purchasePrice: "",
  // Optional batch (create and edit)
  batchNumber: "",
  expiryDate: "",
  quantity: "",
};

const requireFields = ["drugName"];
const batchFields = ["batchNumber", "expiryDate", "quantity"];

// True when the user has started filling the batch section
const hasAnyBatchInput = (values) =>
  batchFields.some((f) => values[f] !== "" && values[f] != null);

const validate = (values) => {
  const errors = {};
  requireFields.forEach((field) => {
    if (!values[field]) return (errors[field] = "Required");
  });

  // If any batch field is filled, the whole batch (and prices) must be complete
  if (hasAnyBatchInput(values)) {
    batchFields.forEach((field) => {
      if (!values[field]) errors[field] = "Required";
    });
    if (values.quantity !== "" && Number(values.quantity) <= 0) {
      errors.quantity = "Must be greater than 0";
    }
    ["salesPrice", "purchasePrice"].forEach((field) => {
      if (values[field] === "" || values[field] == null) {
        errors[field] = "Required when adding a batch";
      }
    });
  }
  return errors;
};

// Used by create. Strips empty batch fields so the backend only creates a batch when complete.
const formatData = (data) => {
  const copy = { ...data };

  if (hasAnyBatchInput(copy)) {
    copy.expiryDate = new Date(copy.expiryDate).toISOString();
  } else {
    batchFields.forEach((f) => delete copy[f]);
  }

  if (!copy.filename) {
    delete copy.filename;
    return copy;
  }
  const formData = new FormData();
  for (const key in copy) {
    formData.append(key, copy[key]);
  }
  return formData;
};

function CreateNewProductModal({ closeModal, isEditMode = false, initialData = null }) {
  const queryClient = useQueryClient();

  // Initial values (populated when editing)
  const formValues = useMemo(() => {
    if (isEditMode && initialData) {
      // Safely get an ID whether the field is populated (object) or a plain string ID
      const getId = (field) => {
        const val = initialData[field];
        if (!val) return "";
        if (typeof val === "object") return val._id || val.id || "";
        return val;
      };

      return {
        drugName: initialData.drugName || "",
        drugType: getId("drugType"),
        unit: initialData.unit || "",
        genericName: getId("genericName"),
        strength: initialData.strength || "",
        brandName: initialData.brandName || "",
        prodcutDescription: initialData.prodcutDescription || "",
        filename: "",
        salesPrice: initialData.salesPrice || "",
        purchasePrice: initialData.purchasePrice || "",
        // Batch fields always start empty so a batch is only added on purpose
        batchNumber: "",
        expiryDate: "",
        quantity: "",
      };
    }
    return defaultInitialValues;
  }, [isEditMode, initialData]);

  // Resolve a display option from the populated initialData object
  const getInitialOption = (field, idValue) => {
    if (!initialData || !initialData[field]) return null;
    const initialField = initialData[field];
    if (
      typeof initialField === "object" &&
      (initialField._id === idValue || initialField.id === idValue)
    ) {
      return { name: initialField.name || initialField.activeIngredient, value: idValue };
    }
    return null;
  };

  const {
    handleChange,
    values,
    touched,
    errors,
    handleBlur,
    setFieldTouched,
    resetForm,
    setValues,
    setFieldValue,
  } = useFormik({
    initialValues: formValues,
    enableReinitialize: true,
    validate: (values) => validate(values),
  });

  const { data: drugTypes } = useCustomQuery(
    GET_DRUG_TYPES,
    { url: `/drugtype/getall`, method: "get", avoidCancelling: true },
    {
      refetchOnWindowFocus: false,
      select: (data) => data.data.map((drug) => ({ name: drug.name, value: drug._id })),
    }
  );

  const { data: genericNames } = useCustomQuery(
    GET_PHARMACY_GENERICS,
    { url: `/generic/getall`, method: "get", avoidCancelling: true },
    {
      refetchOnWindowFocus: false,
      select: (data) =>
        data.data.map((generic) => ({ name: generic.activeIngredient, value: generic._id })),
    }
  );

  // Create mutation
  const { mutate: createProduct, isLoading: createProductLoading } = useCustomMutation(
    {
      url: `/product/create`,
      method: "post",
      data: formatData(values),
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_ALL_PHARMACY_PRODUCTS] });
        toast.success("Success");
        resetForm();
        closeModal();
      },
      onError: (error) => {
        if (Array.isArray(error.message)) return error.message.map((msg) => toast.error(msg));
        return toast.error(error.message);
      },
    }
  );

  // Edit mutation
  const { mutate: updateProduct, isLoading: updateProductLoading } = useCustomMutation(
    {
      url: `/product/edit/${initialData?._id}`,
      method: "patch",
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_ALL_PHARMACY_PRODUCTS] });
        toast.success("Product Updated Successfully");
        resetForm();
        closeModal();
      },
      onError: (error) => {
        if (Array.isArray(error.message)) return error.message.map((msg) => toast.error(msg));
        return toast.error(error.message);
      },
    }
  );

  const onClickSave = () => {
    let emptyRequiredFieldNumber = 0;
    requireFields.forEach((field) => {
      emptyRequiredFieldNumber =
        values[field] === "" ? emptyRequiredFieldNumber + 1 : emptyRequiredFieldNumber;
      setFieldTouched(field, true, true);
    });

    if (emptyRequiredFieldNumber > 0) return;

    // Validate the optional batch section (create AND edit)
    if (hasAnyBatchInput(values)) {
      const batchErrors = validate(values);
      const keys = [...batchFields, "salesPrice", "purchasePrice"];
      keys.forEach((f) => setFieldTouched(f, true, true));
      if (keys.some((f) => batchErrors[f])) {
        toast.error("Complete the batch details (and prices) or clear them.");
        return;
      }
    }

    if (isEditMode) {
      const payload = { ...values };
      delete payload.filename; // backend forbids it in the JSON body

      if (hasAnyBatchInput(values)) {
        payload.expiryDate = new Date(values.expiryDate).toISOString();
        payload.quantity = Number(values.quantity);
      } else {
        batchFields.forEach((f) => delete payload[f]);
      }
      updateProduct(payload);
    } else {
      createProduct();
    }
  };

  const handleSelectDate = (date, name) => {
    setFieldValue(name, date);
  };

  const onAddImg = (file) => {
    setValues((prev) => ({ ...prev, filename: file[0] }));
  };

  return (
    <Stack spacing={2}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <CustomTextInput
            title="Drug Name"
            value={values.drugName}
            placeholder={"Enter drug name"}
            handleChange={handleChange}
            name="drugName"
            {...handleErrorProps(touched.drugName, errors.drugName)}
            onBlur={handleBlur}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Stack spacing={0.5}>
            <Typography variant="body1">Drug Type</Typography>
            <Autocomplete
              freeSolo
              options={drugTypes || []}
              getOptionLabel={(option) => option.name || option}
              value={
                (drugTypes || []).find((opt) => opt.value === values.drugType) ||
                getInitialOption("drugType", values.drugType) ||
                values.drugType
              }
              onChange={(event, newValue) => {
                const val = typeof newValue === "string" ? newValue : newValue?.value;
                setFieldValue("drugType", val);
              }}
              onInputChange={(event, newInputValue) => {
                if (event && event.type === "change") setFieldValue("drugType", newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or Type Manual"
                  error={Boolean(touched.drugType && errors.drugType)}
                  helperText={touched.drugType && errors.drugType}
                  sx={{
                    "& .MuiOutlinedInput-root": { padding: "6px !important", backgroundColor: "#fff" },
                  }}
                />
              )}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Stack spacing={0.5}>
            <Typography variant="body1">Unit</Typography>
            <Autocomplete
              freeSolo
              options={["TABLET", "VIAL", "PIECE", "CAPSULE"]}
              value={values.unit || ""}
              onChange={(e, val) => setFieldValue("unit", val || "")}
              onInputChange={(e, val) => {
                if (e?.type === "change") setFieldValue("unit", val || "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{
                    "& .MuiOutlinedInput-root": { padding: "6px !important", backgroundColor: "#fff" },
                  }}
                />
              )}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Stack spacing={0.5}>
            <Typography variant="body1">Generic Name</Typography>
            <Autocomplete
              freeSolo
              options={genericNames || []}
              getOptionLabel={(option) => option.name || option}
              value={
                (genericNames || []).find((opt) => opt.value === values.genericName) ||
                getInitialOption("genericName", values.genericName) ||
                values.genericName
              }
              onChange={(e, val) =>
                setFieldValue("genericName", typeof val === "string" ? val : val?.value)
              }
              onInputChange={(e, val) => {
                if (e?.type === "change") setFieldValue("genericName", val);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{
                    "& .MuiOutlinedInput-root": { padding: "6px !important", backgroundColor: "#fff" },
                  }}
                />
              )}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} sm={6}>
          <CustomTextInput
            title="Brand Name"
            value={values.brandName}
            name="brandName"
            placeholder={"Enter text here"}
            handleChange={handleChange}
            {...handleErrorProps(touched.brandName, errors.brandName)}
            onBlur={handleBlur}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <CustomTextInput
            title="Drug Strength"
            value={values.strength}
            name="strength"
            placeholder={"Enter text here"}
            handleChange={handleChange}
            {...handleErrorProps(touched.strength, errors.strength)}
            onBlur={handleBlur}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <CustomTextInput
            title="Sales Price"
            value={values.salesPrice}
            name="salesPrice"
            type="number"
            placeholder={"0.00"}
            handleChange={handleChange}
            {...handleErrorProps(touched.salesPrice, errors.salesPrice)}
            onBlur={handleBlur}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <CustomTextInput
            title="Purchase Price"
            value={values.purchasePrice}
            name="purchasePrice"
            type="number"
            placeholder={"0.00"}
            handleChange={handleChange}
            {...handleErrorProps(touched.purchasePrice, errors.purchasePrice)}
            onBlur={handleBlur}
          />
        </Grid>

        <Grid item xs={12}>
          <CustomTextInput
            title="Product Description"
            value={values.prodcutDescription}
            name="prodcutDescription"
            placeholder={"Enter text here"}
            handleChange={handleChange}
            multiline
            helperText={`${values.prodcutDescription.length}/250`}
          />
        </Grid>
      </Grid>

      {/* Optional batch: create and edit */}
      <Stack spacing={1}>
        <Typography variant="heading">
          {isEditMode ? "Add New Batch (Optional)" : "Initial Stock (Optional)"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isEditMode
            ? "Fill these to add a new batch to this product. Leave empty to only update the product details."
            : "Fill these to create the first batch together with the product."}{" "}
          Sales and purchase prices above are used for the batch.
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4}>
            <CustomTextInput
              title="Batch Number"
              value={values.batchNumber}
              name="batchNumber"
              placeholder="Enter batch number"
              handleChange={handleChange}
              {...handleErrorProps(touched.batchNumber, errors.batchNumber)}
              onBlur={handleBlur}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CustomDatePicker
              type="date"
              views={["year", "month", "day"]}
              title="Expiry Date"
              placeholder="Enter Expiry Date"
              disableFuture={false}
              datePickerRootSx={{ height: "auto" }}
              datePickerSx={{ width: "100%" }}
              name="expiryDate"
              id="expiryDate"
              setdate={handleSelectDate}
              date={values.expiryDate}
              onOpen={() => setFieldTouched("expiryDate", true)}
              {...handleErrorProps(touched.expiryDate, errors.expiryDate)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CustomTextInput
              title="Quantity"
              value={values.quantity}
              name="quantity"
              type="number"
              placeholder="Enter quantity"
              handleChange={handleChange}
              inputProps={{ inputProps: { min: 0 } }}
              {...handleErrorProps(touched.quantity, errors.quantity)}
              onBlur={handleBlur}
            />
          </Grid>
        </Grid>
      </Stack>

      {/* Image upload: create mode only */}
      {!isEditMode && (
        <Stack spacing={1} width="fit-content">
          <Typography variant="heading">Add Image</Typography>
          <ImageUpload
            imgTitle=""
            imgStyle={{ borderRadius: "0%" }}
            onAddImg={onAddImg}
            blanckImg="/imgs/black-solid-icon-medicine.jpg"
          />
        </Stack>
      )}

      <Stack spacing={1} direction="row" width="fit-content">
        <CustomButton
          text={isEditMode ? "Update" : "Create"}
          color="secondary"
          onClick={onClickSave}
          disabled={createProductLoading || updateProductLoading}
        />
        <CustomButton text={"Cancel"} variant="containedBrown" onClick={closeModal} />
      </Stack>
    </Stack>
  );
}

export default CreateNewProductModal;